import { useState } from 'react';
import { notification } from 'antd';
import moment from 'moment';
import APIClient from '../api/api';
import { InboundEmail, InboundEmailAttachment } from '../api/models';
import { EmailRoutingDetails } from './EmailRouting';

interface Props {
  email: InboundEmail;
  onTagsChanged?: () => void;
  /** Render extra buttons in the header (e.g. close, prev/next). */
  headerActions?: React.ReactNode;
}

function extractHtmlFromPayload(payload: string | null): string | null {
  if (!payload) return null;
  try {
    const json = atob(payload);
    const parsed = JSON.parse(json);
    const html = parsed?.ParsedValues?.html;
    return typeof html === 'string' && html.trim() ? html : null;
  } catch {
    return null;
  }
}

export default function EmailDetailContent({ email, onTagsChanged, headerActions }: Props) {
  const [removingTag, setRemovingTag] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');
  const [reloadKey, setReloadKey] = useState(0);
  const [currentEmail, setCurrentEmail] = useState(email);

  // Keep in sync if parent changes the email prop
  if (email.id !== currentEmail.id) {
    setCurrentEmail(email);
    setViewMode('html');
  }

  const htmlBody = extractHtmlFromPayload(currentEmail.payload);
  const hasHtml = htmlBody !== null;

  async function removeTag(tag: string) {
    setRemovingTag(tag);
    try {
      await APIClient.deleteEmailTag(currentEmail.id, tag);
      const refreshed = await APIClient.getEmail(currentEmail.id);
      setCurrentEmail(refreshed);
      setReloadKey((k) => k + 1);
      onTagsChanged?.();
    } catch {
      notification.error({ message: 'Failed to remove tag.' });
    } finally {
      setRemovingTag(null);
    }
  }

  return (
    <>
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-border">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-text leading-snug">
              {currentEmail.subject || <span className="text-text-3">(no subject)</span>}
            </div>
            <div className="flex items-center gap-2 mt-2 text-[12px] text-text-3">
              <span className="font-mono">{moment(currentEmail.sentAt).format('MMM D, YYYY · HH:mm')}</span>
            </div>
          </div>
          {headerActions && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {headerActions}
            </div>
          )}
        </div>

        {/* Tags */}
        {currentEmail.tags && currentEmail.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" key={reloadKey}>
            {currentEmail.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10.5px] font-medium bg-accent-soft text-accent"
              >
                {tag}
                <button
                  type="button"
                  disabled={removingTag === tag}
                  onClick={() => removeTag(tag)}
                  className="bg-transparent border-0 p-0 cursor-pointer text-accent/50 hover:text-accent leading-none text-[10px] disabled:opacity-30"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="px-6 py-2 border-b border-border bg-bg flex flex-col gap-0.5 text-[12px]">
        <div className="flex gap-2">
          <span className="text-text-3 w-[36px] shrink-0 text-right">From</span>
          <EmailAddress value={currentEmail.from} />
        </div>
        <div className="flex gap-2">
          <span className="text-text-3 w-[36px] shrink-0 text-right">To</span>
          {currentEmail.to.length === 0
            ? <span className="text-text-3">Unknown</span>
            : <EmailAddress value={currentEmail.to} />
          }
        </div>
        {currentEmail.venue && (
          <div className="flex gap-2">
            <span className="text-text-3 w-[36px] shrink-0 text-right">Venue</span>
            <span className="text-text-2">
              {currentEmail.venue}
              {currentEmail.account && <span className="text-text-3"> · {currentEmail.account}</span>}
            </span>
          </div>
        )}
      </div>

      <EmailRoutingDetails email={currentEmail} />

      {/* Attachments */}
      {currentEmail.attachments && currentEmail.attachments.length > 0 && (
        <div className="px-6 py-2.5 border-b border-border bg-bg flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">Attachments</span>
          {currentEmail.attachments.map((a: InboundEmailAttachment) => (
            <a
              key={a.id}
              target="_blank"
              rel="noreferrer"
              href={APIClient.getAttachmentDownloadUrl(a)}
              className="text-accent text-[12px] hover:opacity-80 font-mono"
            >
              {a.filename}
            </a>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="flex flex-col flex-1 min-h-0">
        {hasHtml && (
          <div className="px-6 pt-3 pb-1 flex gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('html')}
              className={`px-2 py-0.5 rounded text-[11px] font-medium border cursor-pointer ${
                viewMode === 'html'
                  ? 'bg-accent-soft text-accent border-accent/30'
                  : 'bg-transparent text-text-3 border-border hover:text-text-2'
              }`}
            >
              HTML
            </button>
            <button
              type="button"
              onClick={() => setViewMode('text')}
              className={`px-2 py-0.5 rounded text-[11px] font-medium border cursor-pointer ${
                viewMode === 'text'
                  ? 'bg-accent-soft text-accent border-accent/30'
                  : 'bg-transparent text-text-3 border-border hover:text-text-2'
              }`}
            >
              Plain text
            </button>
          </div>
        )}
        <div className="px-6 py-5 flex-1 min-h-0 overflow-auto">
          {hasHtml && viewMode === 'html' ? (
            <div className="rounded-md overflow-hidden border border-border" style={{ height: '100%' }}>
              <iframe
                sandbox=""
                srcDoc={`<!DOCTYPE html><html><head><style>body{margin:0;padding:8px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;line-height:1.5;color:#222;background:#fff;word-break:break-word}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${htmlBody}</body></html>`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Email body"
              />
            </div>
          ) : (
            <pre className="whitespace-pre-wrap text-text text-[12.5px] font-sans m-0 leading-relaxed">
              {currentEmail.text}
            </pre>
          )}
        </div>
      </div>
    </>
  );
}

function parseEmailAddress(raw: string): { name: string; email: string } | null {
  const match = raw.match(/^(.+?)\s*<([^>]+)>$/);
  if (match) {
    const name = match[1].replace(/^["']|["']$/g, '').trim();
    const email = match[2].trim();
    if (name && email.includes('@')) return { name, email };
  }
  return null;
}

function EmailAddress({ value }: { value: string }) {
  const parsed = parseEmailAddress(value);
  if (parsed) {
    return (
      <div className="flex flex-col">
        <span className="text-text">{parsed.name}</span>
        <span className="text-text-3 font-mono text-[11px]">{parsed.email}</span>
      </div>
    );
  }
  return <span className="text-text-2 font-mono break-all">{value}</span>;
}
